import {
  ApdmCampaignDailyRef,
  ApdmCampaignDefinition,
  ApdmCampaignKind,
  ApdmCampaignMode,
  ApdmCampaignRecipientFacts,
  ApdmCampaignRecipientSource,
  ApdmCampaignStatus,
  ApdmCampaignVersionRef,
  ApdmOutreachState,
  ApdmReachability,
  ApdmSendState,
} from './campaign';
import { ApdmReachedChannel } from './out-reaching';

export interface ApdmCampaignRefRequest {
  campaignId: string;
}
export interface ApdmCampaignListRequest {
  afterCampaignId: string | null;
  limit: number;
  status: ApdmCampaignStatus | null;
}
export interface ApdmCampaignSummary {
  id: string;
  title: string;
  kind: ApdmCampaignKind;
  mode: ApdmCampaignMode;
  status: ApdmCampaignStatus;
  version: number;
  startAt: string | null;
  endAt: string | null;
  /**
   * When its sending next opens: now while today's window is open, else
   * the next window on or after its start. Null once paused or ended.
   */
  nextSendAt: string | null;
  createdAt: string;
  updatedAt: string | null;
}
export interface ApdmCampaignListResult {
  rows: ApdmCampaignSummary[];
  nextAfterCampaignId: string | null;
}
export interface ApdmCampaignRecipientCounts {
  total: number;
  reached: number;
  replied: number;
  converted: number;
  unreachable: number;
  unconfirmed: number;
}
export interface ApdmCampaignCapabilities {
  manageCampaign: boolean;
  resumeAll: boolean;
}
export type ApdmCampaignDetailResult = {
  summary: ApdmCampaignSummary;
  /** Who launched it, by name; null when that member is gone. */
  ownerName: string | null;
  /** The store's sending accounts online now; none holds sending up. */
  liveSendingAccounts: number;
  counts: ApdmCampaignRecipientCounts;
  capabilities?: ApdmCampaignCapabilities;
} & ({ scope: 'full'; draft: ApdmCampaignDefinition } | { scope: 'assigned' });
/** Mirror of backend `ApdmRecipientSearchField`. */
export enum ApdmRecipientSearchField {
  Plate = 'plate',
  CustomerName = 'customer_name',
  /** The plate, the name, the phone, the car model and the VIN. */
  Keyword = 'keyword',
}
/** Mirror of backend `kApdmRecipientSearchMaxLength`. */
export const kApdmRecipientSearchMaxLength = 40;
export interface ApdmCampaignRecipientsRequest extends ApdmCampaignVersionRef {
  afterRecipientId: string | null;
  limit: number;
  outreachState?: ApdmOutreachState | null;
  reachability?: ApdmReachability | null;
  /** The text to look for; null or blank lists everyone. */
  search?: string | null;
  /** What `search` matches; the plate by default. */
  searchBy?: ApdmRecipientSearchField | null;
}
export interface ApdmCampaignRecipientView {
  privateChatId?: string | null;
  /** Where the customer was reached; null before they were. */
  channel?: ApdmReachedChannel | null;
  /** The group they were reached in, when `channel` is the group. */
  chatGroupId?: string | null;
  couponCampaignId?: string | null;
  canManage?: boolean;
  doNotContact?: boolean;
  contactFingerprint?: string;
  leadId?: string | null;
  id: string;
  licensePlateNo: string;
  facts: ApdmCampaignRecipientFacts;
  source: ApdmCampaignRecipientSource[];
  reachability: ApdmReachability;
  outreachState: ApdmOutreachState;
  sendState: ApdmSendState;
  sendSeq: number;
  attempt: number;
  failureReason: string | null;
  joinedAt: string;
  reachedAt: string | null;
  repliedAt: string | null;
  convertedAt: string | null;
  /** The newest message of the customer's chat, either side. */
  lastMessage: ApdmRecipientLastMessage | null;
}
/** Mirror of backend `ApdmRecipientLastMessage`. */
export interface ApdmRecipientLastMessage {
  sender: 'customer' | 'staff' | 'system' | 'rpa';
  text: string;
  sentAt: string;
}
export interface ApdmCampaignRecipientsResult {
  campaignId: string;
  version: number;
  rows: ApdmCampaignRecipientView[];
  nextAfterRecipientId: string | null;
  /** On the first page only; null on the pages after it. */
  filterCounts: ApdmCampaignRecipientFilterCounts | null;
}
/** Mirror of backend `ApdmCampaignRecipientFilterCounts`. */
export interface ApdmCampaignRecipientFilterCounts {
  all: number;
  outreachStates: Record<ApdmOutreachState, number>;
  undelivered: number;
}
export enum ApdmAudienceResolveMode {
  Page = 'page',
  Count = 'count',
}
export interface ApdmAudienceSourceFacts {
  source: ApdmCampaignRecipientSource;
  facts: ApdmCampaignRecipientFacts;
}
export interface ApdmAudienceCandidate {
  licensePlateNo: string;
  facts: ApdmCampaignRecipientFacts;
  sources: ApdmAudienceSourceFacts[];
  knownReachable: boolean;
}
export type ApdmCampaignAudienceRequest = ApdmCampaignVersionRef &
  (
    | {
        mode: ApdmAudienceResolveMode.Page;
        afterPlate: string | null;
        limit: number;
      }
    | { mode: ApdmAudienceResolveMode.Count }
  );
export type ApdmAudienceResult = {
  campaignId: string;
  version: number;
  asOf: string;
} & (
  | {
      mode: ApdmAudienceResolveMode.Page;
      candidates: ApdmAudienceCandidate[];
      nextAfterPlate: string | null;
    }
  | { mode: ApdmAudienceResolveMode.Count; total: number }
);
export enum ApdmReachabilityActionOutcome {
  Applied = 'applied',
  Unchanged = 'unchanged',
  Stale = 'stale',
  Closed = 'closed',
}
export interface ApdmReachabilityActionResult {
  campaignId: string;
  version: number;
  outcome: ApdmReachabilityActionOutcome;
  changedRecipients: number;
  nextTick: ApdmCampaignDailyRef | null;
}
export interface ApdmCampaignRecipientRecoveryRequest
  extends ApdmCampaignVersionRef {
  recipientId: string;
  expectedAttempt: number;
  expectedSendSeq: number;
}
export interface ApdmCampaignResumeAllRequest extends ApdmCampaignVersionRef {}
export interface ApdmReachabilityListInput extends ApdmCampaignVersionRef {
  afterRecipientId: string | null;
  limit: number;
}
export interface ApdmReachabilityListRow {
  canManage?: boolean;
  recipientId: string;
  licensePlateNo: string;
  customerName: string | null;
  customerPhone: string | null;
  reason: string | null;
  reachability: ApdmReachability;
  sendState: ApdmSendState;
  sendSeq: number;
  attempt: number;
}
export interface ApdmReachabilityListResult {
  campaignId: string;
  version: number;
  rows: ApdmReachabilityListRow[];
  nextAfterRecipientId: string | null;
  knownReachable: number;
  unconfirmed: number;
}
